import { ChevronsUpDown } from 'lucide-react';
import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { ColorSwatch } from '@/components/ColorSwatch';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useNoProjectRules, useProjects } from '@/hooks/use-projects';
import { cn } from '@/lib/cn';
import { NO_PROJECT_LABEL, rankProjects, type PickerOption } from '@/lib/rank-projects';
import { isShiftProject, type PersonalProject, type Project } from '@/types/projects';

interface ProjectPickerProps {
  id?: string;
  value: string | null;
  onChange: (projectId: string | null) => void;
  /** The entry's stored project (edit mode); shown in the trigger even when archived, never listed. */
  currentProject?: Project | null;
  'aria-describedby'?: string;
  /** Label id(s); the form passes "<label id> <id>-value" so the name includes the selected value. */
  'aria-labelledby'?: string;
  /** Sprint 11 (WP-10): list only projects (and No project) where manual entries are allowed. */
  manualOnly?: boolean;
}

export interface ManualEntryChoices {
  projects: PersonalProject[] | undefined;
  noneAllowed: boolean;
  /** false once loaded when neither a project nor No project allows manual entries. */
  anyAllowed: boolean;
}

/** Sprint 11 (§9.6): rules absent (an older payload) mean allowed, as before. */
export function useManualEntryChoices(manualOnly: boolean): ManualEntryChoices {
  const projects = useProjects();
  const noProject = useNoProjectRules();
  // Sprint 13 (§5.3): shift projects are never offered for time (timer, entries, Quick Add).
  const timeProjects = projects.data?.filter((p) => !isShiftProject(p));
  if (!manualOnly) return { projects: timeProjects, noneAllowed: true, anyAllowed: true };
  const list = timeProjects?.filter((p) => p.rules?.manualEntriesAllowed !== false);
  const noneAllowed = noProject.data?.manualEntriesAllowed !== false;
  return { projects: list, noneAllowed, anyAllowed: list === undefined || list.length > 0 || noneAllowed };
}

function optionKey(option: PickerOption): string {
  return option.kind === 'none' ? 'none' : option.project.id;
}

function optionValue(option: PickerOption): string | null {
  return option.kind === 'none' ? null : option.project.id;
}

/** Searchable project combobox (architecture sprint-03 §9.4). Active projects only; no inline create. */
export function ProjectPicker({
  id,
  value,
  onChange,
  currentProject,
  'aria-describedby': describedBy,
  'aria-labelledby': labelledBy,
  manualOnly = false,
}: ProjectPickerProps): JSX.Element {
  const choices = useManualEntryChoices(manualOnly);
  const projects = { data: choices.projects };
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const listId = useId();
  // The form labels the trigger with "<label id> <id>-value" so the name includes the selection.
  const valueId = id ? `${id}-value` : undefined;
  const listRef = useRef<HTMLUListElement>(null);

  const options = useMemo(
    () => rankProjects(projects.data ?? [], query).filter((o) => o.kind !== 'none' || choices.noneAllowed),
    [projects.data, query, choices.noneAllowed],
  );
  const active = projects.data?.find((p) => p.id === value) ?? null;
  const selected: Project | null = active ?? (value !== null && currentProject?.id === value ? currentProject : null);
  const archivedSelection = selected !== null && active === null;

  function openPicker(next: boolean): void {
    setOpen(next);
    if (!next) return;
    setQuery('');
    const initial = rankProjects(projects.data ?? [], '')
      .filter((o) => o.kind !== 'none' || choices.noneAllowed)
      .findIndex((o) => optionValue(o) === value);
    setHighlight(initial >= 0 ? initial : 0);
  }

  function select(option: PickerOption | undefined): void {
    if (!option) return;
    onChange(optionValue(option));
    setOpen(false);
  }

  function moveHighlight(next: number): void {
    setHighlight(next);
    const el = listRef.current?.children[next] as HTMLElement | undefined;
    el?.scrollIntoView?.({ block: 'nearest' });
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    const count = options.length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (count > 0) moveHighlight((highlight + 1) % count);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (count > 0) moveHighlight((highlight - 1 + count) % count);
        break;
      case 'Home':
        event.preventDefault();
        if (count > 0) moveHighlight(0);
        break;
      case 'End':
        event.preventDefault();
        if (count > 0) moveHighlight(count - 1);
        break;
      case 'Enter':
        event.preventDefault();
        select(options[highlight]);
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  }

  const highlighted = options[highlight];
  const activeOptionId = highlighted ? `${listId}-${optionKey(highlighted)}` : undefined;

  return (
    <Popover open={open} onOpenChange={openPicker}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          aria-describedby={describedBy}
          aria-labelledby={labelledBy}
          className="flex w-full items-center gap-2 rounded-lg border border-input bg-field px-3 py-2 text-left text-base leading-6 text-foreground focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 sm:text-sm sm:leading-5"
        >
          {selected ? (
            <>
              <ColorSwatch hex={selected.color} />
              <span id={valueId} className={cn('truncate', archivedSelection && 'text-muted-foreground')}>
                {archivedSelection ? `${selected.name} (archived)` : selected.name}
              </span>
            </>
          ) : (
            <span id={valueId} className="truncate text-muted-foreground">
              {NO_PROJECT_LABEL}
            </span>
          )}
          <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1">
        <Input
          role="combobox"
          aria-label="Search projects"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={activeOptionId}
          aria-autocomplete="list"
          placeholder="Search projects"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
          }}
          onKeyDown={onKeyDown}
          className="mb-1"
        />
        <ul ref={listRef} id={listId} role="listbox" aria-label="Projects" className="max-h-64 overflow-y-auto">
          {options.length === 0 && <li className="px-2 py-1.5 text-xs text-muted-foreground">No matching projects</li>}
          {options.map((option, index) => {
            const isSelected = optionValue(option) === value;
            return (
              <li
                key={optionKey(option)}
                id={`${listId}-${optionKey(option)}`}
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(option)}
                onMouseMove={() => setHighlight(index)}
                className={cn(
                  'flex cursor-default select-none items-center gap-2 rounded px-2 py-1.5 text-sm text-muted-foreground',
                  index === highlight && 'bg-accent text-foreground',
                  isSelected && 'text-foreground',
                )}
              >
                {option.kind === 'none' ? (
                  <span className="text-muted-foreground">{NO_PROJECT_LABEL}</span>
                ) : (
                  <>
                    <ColorSwatch hex={option.project.color} size={8} />
                    <span className="truncate">{option.project.name}</span>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
